import { Select, MultiSelect, TextInput } from '@mantine/core'
import { ReactQuery } from 'cms-api'
import { Field, Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import {
  ROUTE,
  TRADE_APPLICATION_SURVEY,
  HOW_DID_YOU_HEAR_ABOUT_US_OPTIONS,
  INTERESTED_IN_CATEGORIES_OPTIONS,
  CURRENT_COMPANY_WORKLOAD_OPTIONS,
  WHICH_PERKS_MATTER_THE_MOST_OPTIONS,
} from 'shared-constants'
import * as Yup from 'yup'
import { getPublicLayout } from '@public/components/PublicLayout'
import { Button } from 'components/Button'
import { Box, Flex } from 'components/Layout'
import { Text } from 'components/Typography'
import { HUBSPOT_SCHEDULE_CALL_URL } from 'constants/common'
import { COLOR_INTENT, FONT_SIZE, SPACE } from 'Theme'

export const TradeConfirmation = () => {
  const router = useRouter()
  const [isSurveySubmitted, setIsSurveySubmitted] = useState(false)
  const tradeApplicationId = router.query?.tradeApplicationId as string
  const postTradeApplicationSurvey = ReactQuery.usePostTradeApplicationSurveys()

  return (
    <Flex
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      my={SPACE.PX_40}
    >
      {isSurveySubmitted ? (
        <Flex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          mt={SPACE.PX_40}
        >
          <Text
            fontSize={{ MOBILE: FONT_SIZE.PX_24, DESKTOP: FONT_SIZE.PX_32 }}
            fontWeight={600}
          >
            Thank You!
          </Text>
          <Text mt={SPACE.PX_8} fontSize={FONT_SIZE.PX_16}>
            We’ll be in touch ASAP. If you need immediate support or want to
            learn more about maximizing your Trade Perks experience, our team is
            here to help.
          </Text>
          <Flex
            justifyContent="center"
            mt={SPACE.PX_16}
            mb={SPACE.PX_32}
            gap={SPACE.PX_16}
          >
            <Button
              width="initial"
              onClick={() => {
                router.push(ROUTE.SHOWROOM())
              }}
            >
              Shop now
            </Button>
            <Button
              width="initial"
              variant="outline"
              onClick={() => {
                window.open(HUBSPOT_SCHEDULE_CALL_URL, '_blank')
              }}
            >
              Schedule a call
            </Button>
          </Flex>
        </Flex>
      ) : (
        <>
          <Text
            fontSize={{ MOBILE: FONT_SIZE.PX_24, DESKTOP: FONT_SIZE.PX_32 }}
            fontWeight={600}
          >
            Thanks for submitting your Trade Application!
          </Text>
          <Text mt={SPACE.PX_8} fontSize={FONT_SIZE.PX_20}>
            Help us speed things up—this will only take a minute.
          </Text>
          <Formik
            initialValues={{
              designSpeciality: '',
              projectsCount: '',
              whatBringsYouToTheSite: '',
              teamSize: '',
              howDidYouHearAboutUs: [],
              furnishingsBudget: '',
              interestedInCategories: [],
              currentCompanyWorkload: '',
              whichPerksMatterTheMost: [],
              whichPerksMatterTheMostOther: '',
            }}
            validationSchema={Yup.object().shape({
              atLeastOne: Yup.mixed().test(
                'at-least-one-field',
                'At least one answer must be filled out',
                function () {
                  const {
                    designSpeciality,
                    projectsCount,
                    whatBringsYouToTheSite,
                    teamSize,
                    howDidYouHearAboutUs,
                    // eslint-disable-next-line react/no-this-in-sfc
                  } = this.parent

                  return (
                    designSpeciality ||
                    projectsCount ||
                    whatBringsYouToTheSite ||
                    teamSize ||
                    howDidYouHearAboutUs
                  )
                }
              ),
            })}
            onSubmit={async (values) => {
              if (values) {
                const res = await postTradeApplicationSurvey.mutateAsync({
                  data: {
                    data: {
                      projectsCount: values.projectsCount,
                      whatBringsYouToTheSite: values.whatBringsYouToTheSite,
                      teamSize: values.teamSize,
                      tradeApplication: tradeApplicationId,
                      howDidYouHearAboutUs: values.howDidYouHearAboutUs,
                      interestedInCategories: values.interestedInCategories,
                      furnishingsBudget: values.furnishingsBudget,
                      currentCompanyWorkload:
                        values.currentCompanyWorkload as ReactQuery.TradeApplicationSurvey['currentCompanyWorkload'],
                      whichPerksMatterTheMost: values.whichPerksMatterTheMost,
                      whichPerksMatterTheMostOther:
                        values.whichPerksMatterTheMostOther,
                    },
                  },
                })
                if (res?.data) {
                  setIsSurveySubmitted(true)
                }
              }
            }}
          >
            {({
              handleSubmit,
              setFieldValue,
              isSubmitting,
              errors,
              values,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Flex flexDirection="column" mt={SPACE.PX_40}>
                  <Box mb={SPACE.PX_16}>
                    <Field name="whatBringsYouToTheSite">
                      {({ field, meta }) => (
                        <Select
                          name={field.name}
                          label={
                            TRADE_APPLICATION_SURVEY.whatBringsYouToTheSite
                              .question
                          }
                          placeholder="Select option..."
                          data={TRADE_APPLICATION_SURVEY.whatBringsYouToTheSite.answers.map(
                            (answer) => ({ label: answer, value: answer })
                          )}
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  <Box mb={SPACE.PX_16}>
                    <Field name="interestedInCategories">
                      {({ field, meta }) => (
                        <MultiSelect
                          name={field.name}
                          label={
                            TRADE_APPLICATION_SURVEY.interestedInCategories
                              .question
                          }
                          placeholder="Select option..."
                          data={INTERESTED_IN_CATEGORIES_OPTIONS}
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  <Box mb={SPACE.PX_16}>
                    <Field name="teamSize">
                      {({ field, meta }) => (
                        <Select
                          name={field.name}
                          label={TRADE_APPLICATION_SURVEY.teamSize.question}
                          data={TRADE_APPLICATION_SURVEY.teamSize.answers.map(
                            (answer) => ({ label: answer, value: answer })
                          )}
                          placeholder="Select option..."
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  <Box mb={SPACE.PX_16}>
                    <Field name="projectsCount">
                      {({ field, meta }) => (
                        <Select
                          name={field.name}
                          label={
                            TRADE_APPLICATION_SURVEY.projectsCount.question
                          }
                          placeholder="Select option..."
                          data={TRADE_APPLICATION_SURVEY.projectsCount.answers.map(
                            (answer) => ({ label: answer, value: answer })
                          )}
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  <Box mb={SPACE.PX_16}>
                    <Field name="furnishingsBudget">
                      {({ field, meta }) => (
                        <Select
                          name={field.name}
                          label={
                            TRADE_APPLICATION_SURVEY.furnishingsBudget.question
                          }
                          placeholder="Select option..."
                          data={TRADE_APPLICATION_SURVEY.furnishingsBudget.answers.map(
                            (answer) => ({ label: answer, value: answer })
                          )}
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  <Box mb={SPACE.PX_16}>
                    <Field name="currentCompanyWorkload">
                      {({ field, meta }) => (
                        <Select
                          name={field.name}
                          label={
                            TRADE_APPLICATION_SURVEY.currentCompanyWorkload
                              .question
                          }
                          placeholder="Select option..."
                          data={CURRENT_COMPANY_WORKLOAD_OPTIONS}
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  <Box mb={SPACE.PX_16}>
                    <Field name="howDidYouHearAboutUs">
                      {({ field, meta }) => (
                        <MultiSelect
                          name={field.name}
                          label={
                            TRADE_APPLICATION_SURVEY.howDidYouHearAboutUs
                              .question
                          }
                          placeholder="Select option..."
                          data={HOW_DID_YOU_HEAR_ABOUT_US_OPTIONS}
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  <Box mb={SPACE.PX_16}>
                    <Field name="whichPerksMatterTheMost">
                      {({ field, meta }) => (
                        <MultiSelect
                          name={field.name}
                          label={
                            TRADE_APPLICATION_SURVEY.whichPerksMatterTheMost
                              .question
                          }
                          placeholder="Select option..."
                          data={WHICH_PERKS_MATTER_THE_MOST_OPTIONS}
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  {values.whichPerksMatterTheMost.includes('OTHER') && (
                    <Box mb={SPACE.PX_16}>
                      <Field name="whichPerksMatterTheMostOther">
                        {({ field, meta }) => (
                          <TextInput
                            name={field.name}
                            label={
                              TRADE_APPLICATION_SURVEY
                                .whichOtherPerksMatterTheMost.question
                            }
                            placeholder="Enter value..."
                            error={
                              meta.touched && meta.error
                                ? meta.error
                                : undefined
                            }
                            value={field.value}
                            onChange={(value) => {
                              setFieldValue(
                                field.name,
                                value.currentTarget.value
                              )
                            }}
                          />
                        )}
                      </Field>
                    </Box>
                  )}

                  {/* @ts-ignore */}
                  {errors.atLeastOne && (
                    <Text
                      fontSize={FONT_SIZE.PX_12}
                      color={COLOR_INTENT.ERROR_DARK}
                    >
                      {/* @ts-ignore */}
                      {errors.atLeastOne}
                    </Text>
                  )}
                  <Flex
                    mt={SPACE.PX_15}
                    display="flex"
                    justifyContent="flex-start"
                  >
                    <Button
                      // add loading state
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                      width="100%"
                      type="submit"
                      label="Submit Trade Survey"
                      margin={0}
                    >
                      Submit
                    </Button>
                  </Flex>
                </Flex>
              </Form>
            )}
          </Formik>
        </>
      )}
    </Flex>
  )
}

TradeConfirmation.seoProps = {
  description: 'Thanks for submitting your Trade Application!',
}

TradeConfirmation.getLayout = (page) =>
  getPublicLayout(page, {
    isFullWidth: true,
  })
